'use client'

import React from 'react'
import Link from 'next/link'
import { useDownloadGate } from '@/lib/downloads/useDownloadGate'
import { useMemberFileHref } from '@/lib/downloads/useFilePass'
import { fileNameFromUrl } from '@/lib/downloads/fileLinks'
import { LockSvg } from '@/ui/components/KnowledgeHubV2/icons'

type Props = {
  href: string
  children: React.ReactNode
}

/**
 * Inline rich-text link to a downloadable file. Rich text used to render these as plain
 * anchors, so a "Download: …(PDF)" sentence inside a product description bypassed the
 * members-only rule that PdfCard / KnowledgeDownloads already enforce (found on the Cellavie
 * RE-GEN 90 page, 2026-09-16). Same gate as every other download: logged-out visitors go to
 * /login?next=<here>; members get the file and the lead notification fires.
 *
 * Renders the logged-out variant on the server and until the Supabase session hydrates, so
 * anonymous HTML never carries a raw file href (full-qa `raw-download-link` check).
 */
export const GatedFileLink: React.FC<Props> = ({ href, children }) => {
  const { user, isLoaded, loginHref, notifyDownload, trackGateClick, trackFileOpen } =
    useDownloadGate()
  // carries the member's file pass (see lib/downloads/filePass.ts)
  const memberHref = useMemberFileHref(href)
  const fileName = fileNameFromUrl(href)

  if (!user) {
    return (
      <Link
        href={loginHref}
        onClick={(e) => {
          // Session not known yet (the page is still hydrating): hold the tap instead of
          // sending a member to the login page.
          if (!isLoaded) return e.preventDefault()
          trackGateClick(fileName, 'inline_link')
        }}
        className="inline-flex items-baseline gap-1"
        aria-label={`Sign in to download ${fileName}`}
        title="Sign in to download"
      >
        <LockSvg className="w-3.5 h-3.5 self-center shrink-0" />
        <span>{children}</span>
        <span className="text-text-faded whitespace-nowrap">(sign in to download)</span>
      </Link>
    )
  }

  return (
    <a
      href={memberHref}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        notifyDownload({ pdfFilename: fileName, pdfUrl: href })
        trackFileOpen(fileName, href, 'inline_link')
      }}
      aria-label={`Download ${fileName}`}
    >
      {children}
    </a>
  )
}
